import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from './LocalizedLink'
import { cn } from '@/lib/utils'

function NashiSwitch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean
  onChange: () => void
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      className={cn(
        'relative shrink-0 inline-flex h-5 w-10 items-center transition-colors duration-500',
        'border',
        checked
          ? 'bg-bruno border-bruno'
          : 'bg-cream border-argan/50'
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 -translate-y-1/2 h-3 w-3 transition-all duration-500',
          checked
            ? 'left-[calc(100%-0.875rem)] bg-cream'
            : 'left-[3px] bg-argan-deep'
        )}
      />
    </button>
  )
}

export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {!showPreferences && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-5 left-5 right-5 md:right-auto md:bottom-6 md:left-6 md:max-w-[26rem]"
            style={{ zIndex: 9999 }}
            role="dialog"
            aria-label={t('cookie.title', 'Cookie consent')}
          >
            <div className="relative bg-cream/95 backdrop-blur-md border border-argan/30 shadow-[0_24px_60px_-15px_rgba(59,49,46,0.28)]">
              {/* Decorative top hairline */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-argan to-transparent" />

              <div className="px-7 py-7 md:px-8 md:py-8">
                {/* Eyebrow row: editorial number + label */}
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-display italic text-argan-deep text-sm leading-none">
                    01
                  </span>
                  <span className="block w-7 h-px bg-argan-deep/60" />
                  <span className="text-[10px] font-medium tracking-[0.28em] uppercase text-argan-deep">
                    {t('cookie.eyebrow', 'Privacy')}
                  </span>
                </div>

                {/* Title — display italic, editorial */}
                <h2 className="font-display italic text-bruno text-[1.6rem] leading-[1.1] mb-3">
                  {t('cookie.bannerTitle', 'Cookies & consenso.')}
                </h2>

                {/* Body */}
                <p className="text-[13.5px] text-bruno-soft leading-relaxed mb-7">
                  {t('cookie.message')}{' '}
                  <LocalizedLink
                    to="cookiePolicy"
                    className="text-bruno underline decoration-argan-deep/40 underline-offset-4 hover:decoration-bruno transition-colors"
                  >
                    {t('cookie.learnMore')}
                  </LocalizedLink>
                </p>

                {/* Primary CTA — filled bruno */}
                <button
                  onClick={acceptAll}
                  className="group relative w-full inline-flex items-center justify-center gap-2 bg-bruno text-cream py-3.5 px-6 text-[11px] font-medium tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-argan-deep"
                >
                  <span>{t('cookie.acceptAll', 'Accetta tutti')}</span>
                  <span className="transition-transform duration-500 group-hover:translate-x-1">
                    →
                  </span>
                </button>

                {/* Secondary actions — text-link row */}
                <div className="mt-5 pt-5 border-t border-argan/25 flex items-center justify-between gap-3">
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="text-[10.5px] font-medium tracking-[0.22em] uppercase text-bruno-soft hover:text-bruno transition-colors duration-300 border-b border-argan/40 hover:border-bruno pb-0.5"
                  >
                    {t('cookie.customize', 'Personalizza')}
                  </button>
                  <button
                    onClick={rejectAll}
                    className="text-[10.5px] font-medium tracking-[0.22em] uppercase text-bruno-soft hover:text-bruno transition-colors duration-300"
                  >
                    {t('cookie.rejectAll', 'Rifiuta')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences Modal */}
      <AnimatePresence>
        {showPreferences && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 flex items-end md:items-center justify-center bg-bruno/40 backdrop-blur-sm p-0 md:p-6"
            style={{ zIndex: 10000 }}
            onClick={() => setShowPreferences(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-cream w-full md:max-w-lg max-h-[92vh] overflow-y-auto border border-argan/30 shadow-[0_30px_80px_-20px_rgba(59,49,46,0.4)]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative top hairline */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-argan to-transparent" />

              <div className="px-7 py-7 md:px-9 md:py-9">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-7">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-display italic text-argan-deep text-sm leading-none">
                        02
                      </span>
                      <span className="block w-7 h-px bg-argan-deep/60" />
                      <span className="text-[10px] font-medium tracking-[0.28em] uppercase text-argan-deep">
                        {t('cookie.preferencesEyebrow', 'Preferenze')}
                      </span>
                    </div>
                    <h2 className="font-display italic text-bruno text-3xl leading-[1.05]">
                      {t('cookie.preferencesTitle', 'Le tue preferenze.')}
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="shrink-0 p-1.5 text-bruno-soft hover:text-bruno transition-colors duration-300"
                    aria-label={t('aria.close', 'Chiudi')}
                  >
                    <X className="h-5 w-5" strokeWidth={1.25} />
                  </button>
                </div>

                <p className="text-sm text-bruno-soft leading-relaxed mb-8 max-w-[44ch]">
                  {t('cookie.preferencesDescription', 'Gestisci le tue preferenze sui cookie. I cookie necessari sono sempre attivi.')}
                </p>

                {/* Categories */}
                <div className="divide-y divide-argan/20 border-t border-b border-argan/20">
                  {/* Necessary */}
                  <div className="flex items-start justify-between gap-5 py-5">
                    <div className="flex-1 min-w-0">
                      <p className="font-display italic text-bruno text-lg leading-tight mb-1">
                        {t('cookie.necessary', 'Necessari')}
                      </p>
                      <p className="text-xs text-bruno-soft leading-relaxed">
                        {t('cookie.necessaryDesc', 'Essenziali per il funzionamento del sito.')}
                      </p>
                    </div>
                    <span className="shrink-0 mt-1 text-[10px] font-medium tracking-[0.22em] uppercase text-argan-deep">
                      {t('cookie.alwaysOn', 'Sempre attivi')}
                    </span>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between gap-5 py-5">
                    <div className="flex-1 min-w-0">
                      <p className="font-display italic text-bruno text-lg leading-tight mb-1">
                        {t('cookie.analytics', 'Analitici')}
                      </p>
                      <p className="text-xs text-bruno-soft leading-relaxed">
                        {t('cookie.analyticsDesc', 'Ci aiutano a capire come usi il sito.')}
                      </p>
                    </div>
                    <NashiSwitch
                      checked={tempPrefs.analytics}
                      onChange={() => setTempPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                      ariaLabel={t('cookie.analytics', 'Analitici')}
                    />
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start justify-between gap-5 py-5">
                    <div className="flex-1 min-w-0">
                      <p className="font-display italic text-bruno text-lg leading-tight mb-1">
                        {t('cookie.marketing', 'Marketing')}
                      </p>
                      <p className="text-xs text-bruno-soft leading-relaxed">
                        {t('cookie.marketingDesc', 'Per contenuti e pubblicità personalizzati.')}
                      </p>
                    </div>
                    <NashiSwitch
                      checked={tempPrefs.marketing}
                      onChange={() => setTempPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                      ariaLabel={t('cookie.marketing', 'Marketing')}
                    />
                  </div>
                </div>

                {/* Footer actions */}
                <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="text-[10.5px] font-medium tracking-[0.22em] uppercase text-bruno-soft hover:text-bruno transition-colors duration-300 self-start sm:self-auto"
                  >
                    {t('cookie.cancel', 'Annulla')}
                  </button>
                  <button
                    onClick={() => {
                      savePreferences(tempPrefs)
                      setShowPreferences(false)
                    }}
                    className="group inline-flex items-center justify-center gap-2 bg-bruno text-cream py-3 px-7 text-[11px] font-medium tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-argan-deep"
                  >
                    <span>{t('cookie.savePreferences', 'Salva preferenze')}</span>
                    <span className="transition-transform duration-500 group-hover:translate-x-1">
                      →
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
